import { getToken, deleteToken, onMessage, type Unsubscribe } from 'firebase/messaging'
import { doc, updateDoc } from 'firebase/firestore'
import { getMessagingInstance, db } from '@/lib/firebase'

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope'

/**
 * Explicitly register the FCM service worker at Firebase's dedicated scope
 * and wait for it to be active.
 */
async function ensureFcmServiceWorker(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: FCM_SW_SCOPE,
  })

  // Wait for the SW to become active
  if (!reg.active) {
    await new Promise<void>((resolve) => {
      const sw = reg.installing || reg.waiting
      if (!sw) { resolve(); return }
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated') resolve()
      })
    })
  }

  return reg
}

/**
 * Delete all Firebase Messaging IndexedDB databases to clear stale
 * token-to-subscription mappings.
 */
async function clearFirebaseMessagingIDB(): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  const dbNames = [
    'firebase-messaging-database',
    'firebase-messaging-store',
  ]
  for (const name of dbNames) {
    try {
      indexedDB.deleteDatabase(name)
    } catch {
      // May not exist
    }
  }
}

/**
 * Register the FCM service worker proactively (no permission required).
 * Safe to call on every app load — idempotent.
 */
export async function registerFcmServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    await ensureFcmServiceWorker()
  } catch {
    // Non-critical — will be retried when user enables notifications
  }
}

/**
 * Request notification permission, get FCM token, and save it to the user's profile.
 */
export async function requestAndSaveFcmToken(uid: string): Promise<boolean> {
  if (typeof Notification === 'undefined') return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const messaging = await getMessagingInstance()
  if (!messaging) return false

  // Delete any existing token to force a fresh registration
  try {
    await deleteToken(messaging)
  } catch {
    // No existing token
  }

  // Explicitly register the FCM SW and pass it to getToken
  const swRegistration = await ensureFcmServiceWorker()

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swRegistration,
  })

  const profileRef = doc(db, 'users', uid, 'profile', 'main')
  await updateDoc(profileRef, { fcmTokens: [token] })

  return true
}

/**
 * Nuclear reset: clear ALL push subscriptions, IndexedDB, service workers,
 * then re-register everything fresh.
 */
export async function resetFcmRegistration(uid: string): Promise<boolean> {
  const messaging = await getMessagingInstance()

  // 1. Delete token from FCM
  if (messaging) {
    try {
      await deleteToken(messaging)
    } catch {
      // No token to delete
    }
  }

  // 2. Clear push subscriptions from ALL service workers (not just FCM ones)
  //    This fixes stale subscriptions from when FCM was previously merged into sw.js
  const registrations = await navigator.serviceWorker.getRegistrations()
  for (const reg of registrations) {
    try {
      const sub = await reg.pushManager.getSubscription()
      if (sub) await sub.unsubscribe()
    } catch {
      // pushManager may not be available
    }

    // Unregister FCM-specific SWs
    if (reg.active?.scriptURL.includes('firebase-messaging-sw') ||
        reg.scope.includes('firebase-cloud-messaging-push-scope')) {
      await reg.unregister()
    }
  }

  // 3. Clear Firebase Messaging IndexedDB (stale token-to-subscription mappings)
  await clearFirebaseMessagingIDB()

  // 4. Clear tokens from Firestore
  const profileRef = doc(db, 'users', uid, 'profile', 'main')
  await updateDoc(profileRef, { fcmTokens: [] })

  // 5. Small delay to let IndexedDB cleanup settle
  await new Promise((r) => setTimeout(r, 500))

  // 6. Re-register with a completely fresh token
  return requestAndSaveFcmToken(uid)
}

/**
 * Subscribe to foreground FCM messages.
 * Shows a device notification via SW and dispatches an event for the in-app banner.
 */
export async function subscribeForegroundMessages(): Promise<Unsubscribe | undefined> {
  const messaging = await getMessagingInstance()
  if (!messaging) return undefined

  return onMessage(messaging, (payload) => {
    const data = payload.data || {}
    const title = data.title || payload.notification?.title
    const body = data.body || payload.notification?.body
    const url = data.url || (payload.fcmOptions as Record<string, string> | undefined)?.link

    if (!title) return

    // Show device-level notification via FCM service worker
    if (Notification.permission === 'granted') {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        const fcmReg = registrations.find((r) =>
          r.scope.includes('firebase-cloud-messaging-push-scope') ||
          r.active?.scriptURL.includes('firebase-messaging-sw'),
        )
        const reg = fcmReg || registrations[0]
        if (reg) {
          reg.showNotification(title, {
            body: body || '',
            icon: '/icons/icon-192.png',
            data: { url: url || '/' },
          })
        }
      })
    }

    // Dispatch for in-app banner
    window.dispatchEvent(
      new CustomEvent('fcm-foreground', { detail: { title, body, url } }),
    )
  })
}
